import React, { useContext, useEffect, useState } from "react";

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Menu,
  MenuItem,
  TextField
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ConfirmationModal from "../ConfirmationModal";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import toastError from "../../errors/toastError";

const MessageOptionsMenu = ({
  message,
  menuOpen,
  handleClose,
  anchorEl
}) => {
  const { setReplyingMessage } = useContext(ReplyMessageContext);

  const [confirmationOpen, setConfirmationOpen] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editBody, setEditBody] = useState("");
  const [messagePrefix, setMessagePrefix] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const [forwardOpen, setForwardOpen] = useState(false);
  const [forwardOptions, setForwardOptions] = useState([]);
  const [forwardSearchParam, setForwardSearchParam] = useState("");
  const [selectedForwardContact, setSelectedForwardContact] = useState(null);
  const [forwardSearching, setForwardSearching] = useState(false);
  const [forwardSending, setForwardSending] = useState(false);

  useEffect(() => {
    if (!forwardOpen) return undefined;

    const search = forwardSearchParam.trim();

    if (search.length < 3) {
      setForwardOptions([]);
      setForwardSearching(false);
      return undefined;
    }

    let active = true;
    setForwardSearching(true);

    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/contacts", {
          params: { searchParam: search }
        });

        if (active) {
          setForwardOptions(
            Array.isArray(data && data.contacts) ? data.contacts : []
          );
        }
      } catch (err) {
        if (active) toastError(err);
      } finally {
        if (active) setForwardSearching(false);
      }
    }, 500);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [forwardOpen, forwardSearchParam]);

  const handleDeleteMessage = async () => {
    try {
      await api.delete(`/messages/${message.id}`);
    } catch (err) {
      toastError(err);
    }
  };

  const hanldeReplyMessage = () => {
    setReplyingMessage(message);
    sessionStorage.setItem("replyingMessage", JSON.stringify(message));
    handleClose();
  };

  const handleOpenConfirmationModal = () => {
    setConfirmationOpen(true);
    handleClose();
  };

  const handleOpenEditModal = () => {
    const currentBody =
      message && typeof message.body === "string" ? message.body : "";

    const prefixMatch = currentBody.match(/^\*[^*]+:\*\s*(?:\r?\n)?/);
    const prefix = prefixMatch ? prefixMatch[0] : "";

    setMessagePrefix(prefix);
    setEditBody(currentBody.slice(prefix.length));
    setEditOpen(true);
    handleClose();
  };

  const handleCloseEditModal = () => {
    if (savingEdit) return;

    setEditOpen(false);
    setEditBody("");
    setMessagePrefix("");
  };

  const handleEditMessage = async () => {
    const correctedText = editBody.trim();

    if (!correctedText || !message || !message.id) return;

    try {
      setSavingEdit(true);

      await api.put(`/messages/${message.id}`, {
        body: `${messagePrefix}${correctedText}`
      });

      toast.success("Correção enviada com sucesso.");

      setEditOpen(false);
      setEditBody("");
      setMessagePrefix("");
    } catch (err) {
      toastError(err);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleOpenForwardModal = () => {
    setForwardOpen(true);
    setForwardOptions([]);
    setForwardSearchParam("");
    setSelectedForwardContact(null);
    handleClose();
  };

  const handleCloseForwardModal = () => {
    if (forwardSending) return;

    setForwardOpen(false);
    setForwardOptions([]);
    setForwardSearchParam("");
    setSelectedForwardContact(null);
    setForwardSearching(false);
  };

  const handleForwardMessage = async () => {
    if (
      !message ||
      !message.id ||
      !selectedForwardContact ||
      !selectedForwardContact.id
    ) {
      return;
    }

    try {
      setForwardSending(true);

      await api.post(`/messages/${message.id}/forward`, {
        destinationContactId: selectedForwardContact.id
      });

      handleCloseForwardModal();
      toast.success("Mensagem reencaminhada com sucesso.");
    } catch (err) {
      toastError(err);
    } finally {
      setForwardSending(false);
    }
  };

  const renderContactLabel = option => {
    if (!option) return "";

    return option.number
      ? `${option.name} - ${option.number}`
      : option.name || "";
  };

  const canEditMessage =
    message &&
    message.fromMe &&
    !message.isDeleted &&
    (!message.mediaType || message.mediaType === "chat") &&
    typeof message.body === "string";

  const canForwardMessage =
    message &&
    message.id &&
    !message.isDeleted;

  return (
    <>
      <ConfirmationModal
        title={i18n.t("messageOptionsMenu.confirmationModal.title")}
        open={confirmationOpen}
        onClose={setConfirmationOpen}
        onConfirm={handleDeleteMessage}
      >
        {i18n.t("messageOptionsMenu.confirmationModal.message")}
      </ConfirmationModal>

      <Dialog
        open={editOpen}
        onClose={handleCloseEditModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Corrigir mensagem enviada</DialogTitle>

        <DialogContent>
          <DialogContentText>
            A correção será enviada como uma nova mensagem, preservando o
            histórico da mensagem original.
          </DialogContentText>

          <TextField
            autoFocus
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            label="Texto corrigido"
            value={editBody}
            onChange={event => setEditBody(event.target.value)}
            disabled={savingEdit}
          />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleCloseEditModal}
            disabled={savingEdit}
          >
            Cancelar
          </Button>

          <Button
            onClick={handleEditMessage}
            color="primary"
            variant="contained"
            disabled={savingEdit || !editBody.trim()}
          >
            {savingEdit ? "Enviando..." : "Enviar correção"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={forwardOpen}
        onClose={handleCloseForwardModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Reencaminhar mensagem</DialogTitle>

        <DialogContent dividers>
          <Autocomplete
            options={forwardOptions}
            loading={forwardSearching}
            value={selectedForwardContact}
            getOptionLabel={renderContactLabel}
            getOptionSelected={(option, value) => option.id === value.id}
            onChange={(event, newValue) => {
              setSelectedForwardContact(newValue);
            }}
            onInputChange={(event, newValue) => {
              setForwardSearchParam(newValue || "");
            }}
            noOptionsText={
              forwardSearchParam.trim().length < 3
                ? "Digite pelo menos 3 caracteres"
                : "Nenhum contato encontrado"
            }
            renderInput={params => (
              <TextField
                {...params}
                autoFocus
                variant="outlined"
                label="Pesquisar contato por nome ou número"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {forwardSearching ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleCloseForwardModal}
            disabled={forwardSending}
          >
            Cancelar
          </Button>

          <Button
            onClick={handleForwardMessage}
            color="primary"
            variant="contained"
            disabled={
              forwardSending ||
              !selectedForwardContact ||
              !selectedForwardContact.id
            }
          >
            {forwardSending ? "Reencaminhando..." : "Reencaminhar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={anchorEl}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
        open={menuOpen}
        onClose={handleClose}
      >
        {canEditMessage && (
          <MenuItem onClick={handleOpenEditModal}>
            Enviar correção
          </MenuItem>
        )}

        {canForwardMessage && (
          <MenuItem onClick={handleOpenForwardModal}>
            Reencaminhar
          </MenuItem>
        )}

        {message && message.fromMe && (
          <MenuItem onClick={handleOpenConfirmationModal}>
            {i18n.t("messageOptionsMenu.delete")}
          </MenuItem>
        )}

        <MenuItem onClick={hanldeReplyMessage}>
          {i18n.t("messageOptionsMenu.reply")}
        </MenuItem>
      </Menu>
    </>
  );
};

export default MessageOptionsMenu;
